import { useContext, useEffect, useRef } from "react";

import FilterPanel from "../Common/FilterPanel";
import MetricCard from "./MetricCard";
import FiveStarCard from "./FiveStarCard";
import RecipientMessagesCard from "./RecipientMessagesCard";
//import ResponseTimesCard from "./ResponseTimesCard";
import { AppContexts } from "../../../providers";

/**
 * Each metric gets its own card with KPI summary + chart.
 * chartType picks the best visual for the data:
 *   area  → continuous volume/flow metrics
 *   bar   → discrete countable events, side-by-side comparison
 *   line  → trend lines, especially with total vs unique
 */
const METRICS = [
  {
    title: "Guest Arrivals",
    dataKey: "guest_checkins",
    color: "#14b8a6",
    chartType: "area",
    url: "check-in-out",
  },
  {
    title: "Automated SMS & Email Sent",
    color: "#8b5cf6",
    chartType: "bar",
    url: "automated-sms-sent",
    series: [
      { dataKey: "sent_sms", field: "actions", name: "SMS", color: "#f59e0b" },
      { dataKey: "sent_email", field: "actions", name: "Email", color: "#ec4899" },
    ],
  },
  {
    title: "Text Message Conversations",
    dataKey: "text_threads",
    color: "#0ea5e9",
    chartType: "area",
    url: "message-log",
  },
  {
    title: "Guestbook Views",
    dataKey: "guestbook_views",
    color: "#f59e0b",
    chartType: "line",
    url: "guestbook-views",
    series: [
      { dataKey: "guestbook_views", field: "actions", name: "Total Views", color: "#8b5cf6" },
      { dataKey: "guestbook_views", field: "actions_unique", name: "Unique Views", color: "#8b5cf6", dashed: true },
    ],
  },
  {
    title: "Leads Generated",
    dataKey: "leads_generated",
    color: "#84cc16",
    chartType: "bar",
    url: "webchat",
  },
  {
    title: "Extended Stay Messages Sent",
    dataKey: "extend_stay_messages",
    color: "#f97316",
    chartType: "area",
    url: "extend-stay",
  },
  {
    title: "AI Guest Replies Generated",
    dataKey: "ai_replies",
    color: "#6366f1",
    chartType: "bar",
    url: "reply-logs",
  },
  {
    title: "Work Orders Created",
    dataKey: "work_orders",
    color: "#a855f7",
    chartType: "bar",
    url: "webchat",
  },
];

const SkeletonMetric = () => (
  <div className="overview-metric-card overview-skeleton">
    <div className="skeleton-line skeleton-metric-label" />
    <div className="skeleton-line skeleton-metric-number" />
    <div className="skeleton-line skeleton-metric-footer" />
    <div className="skeleton-chart skeleton-metric-chart" />
  </div>
);

const Overview = () => {
  const ReportsContext = useContext(AppContexts.ReportsContext);
  const { loading, overview, startDate, endDate, getOverview } = ReportsContext;
  const hasFetched = useRef(false);

  useEffect(() => {
    if (!hasFetched.current && startDate && endDate) {
      hasFetched.current = true;
      getOverview(startDate, endDate);
    }
  }, [startDate, endDate]);

  const handleSubmit = (start, end) => {
    if (start && end) {
      getOverview(start, end);
    }
  };

  const hasData = overview && Object.keys(overview).length > 0;
  const isLoading = loading > 0;

  return (
    <li className="reports-overview-tab-content">
      <h2>Overview</h2>
      <div className="uk-card hms-left-tabs-content-card uk-padding-remove">
        <FilterPanel onSubmit={handleSubmit} />

        {/* Metric Cards Grid */}
        {isLoading && (
          <div className="overview-metric-grid">
            {METRICS.map((m) => (
              <SkeletonMetric key={m.title} />
            ))}
          </div>
        )}
        {!isLoading && hasData && (
          <>
            <div className="overview-metric-grid">
              {METRICS.map((m) => (
                <MetricCard
                  key={m.title}
                  title={m.title}
                  dataKey={m.dataKey}
                  data={overview[m.dataKey]}
                  color={m.color}
                  chartType={m.chartType}
                  url={m.url}
                  series={m.series}
                  overview={overview}
                  stacked={m.stacked}
                  startDate={startDate}
                  endDate={endDate}
                />
              ))}
              <FiveStarCard data={overview.fivestar_ratings} />
            </div>

            {/* Categorical Charts — full width */}
            <div className="overview-wide-grid">
              <RecipientMessagesCard data={overview.messages_by_recipient} />
              {/*
              <ResponseTimesCard data={overview.response_times} />
              */}
            </div>
          </>
        )}

        {/* Empty State */}
        {!isLoading && !hasData && (
          <div className="overview-empty">
            <p>Select a date range and click Go to view report data.</p>
          </div>
        )}
      </div>
    </li>
  );
};

export default Overview;
